import type { ApiProduct } from '../../../../models/ecommerce/product/ApiProduct';
import type { PriceType } from '../../models/priceData/priceData';
import type { ProductVariantProps } from '../../models/props/itemTeaserProps';
import type { A11yTextType } from '../A11yTextWrapper/A11yTextWrapper';
import type { ProductSearchListingItemData } from './ProductSearchListingItem.type';

import { mapToProductListingItemData } from '../../../cart/components/molecules/ProductListingItem';
import {
    getProductAttributesA11yText,
    getProductCustomerInformationText,
    getProductPackageSizeInfoText,
} from '../../../ecommerce/components/ProductInfoProperty';
import { buildProductRedirectUrl } from '../../../ecommerce/utils/urlEcommerceUtils';
import { mapVariantPriceData } from '../../models/domain/ecommerce/promotion/promotionMappers';
import { getProductSearchListingItemPriceA11yText } from './ProductSearchListingItemPriceLabels';

export const mapToProductSearchListingItemData = (
    input: ApiProduct,
    variant: ProductVariantProps | undefined,
): ProductSearchListingItemData => {
    const productListingData = mapToProductListingItemData(input);
    const { priceData, promotionPriceData, promotion } = mapVariantPriceData(
        {
            priceData: input.priceData,
            promotionPriceData: input.promotionPriceData,
            promotion: input.promo,
        },
        variant,
    );

    return {
        identifier: input.identifier,
        name: productListingData.name,
        imageUrl: productListingData.imageUrl,
        manufacturer: productListingData.manufacturer,
        url: productListingData.url || buildProductRedirectUrl(input.identifier),
        variants: input.variants,
        packageSize: variant ? undefined : productListingData.packageSize,
        packageSizeInformation: variant ? undefined : productListingData.packageSizeInformation,
        attributeLabels: productListingData.attributeLabels.slice(0, 3),
        consumerInformationText: productListingData.consumerInformationText,
        energyLabel: productListingData.energyLabel,

        historicalPriceData: input.historicalPriceData,
        priceData,
        promotionPriceData,
        promotion,
        recycleFeeData: input.details.recycleFeeData,
        comparativePrice: input.comparativePrice,
        displayLowerPrice: input.displayLowerPrice,
        displayComparativePrice: input.displayWeightPrice,
    };
};

export const getProductSearchListingItemA11yText = (params: {
    product: ProductSearchListingItemData;
    variant: ProductVariantProps | undefined;
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isB2B: boolean;
    isUserMember: boolean;
}) => {
    const priceTexts = getProductSearchListingItemPriceA11yText(params);

    const a11yText = [
        params.product.name,
        params.product.manufacturer,
        !params.variant ? getProductPackageSizeInfoText(params.product)?.a11y : params.variant.name,
        getProductCustomerInformationText(params.product)?.a11y,
        priceTexts?.primaryText,
        priceTexts?.secondaryText,
        getProductAttributesA11yText(params.product),
    ]
        .filter(Boolean)
        .join(', ');

    return a11yText;
};
